import type { 
  PluginMLIntegrationConfig, 
  MLIntegrationAPI, 
  MLModelAPI, 
  PredictionResult, 
  VisualizationConfig
} from './types';
import { 
  createNativeModel,
  nativeFFT, 
  nativeMean, 
  nativeStandardDeviation, 
  nativeCorrelation 
} from './native-algorithms';
import type { 
  PluginManifest, 
  ChartPlugin, 
  PluginContext,
  AfterRenderEvent
} from '../types';
import { exportForecastOverlay } from '../../core/chart/exporter/svg/plugins/regression';

const manifestML: PluginManifest = {
  name: "velo-plot-ml-integration",
  version: "1.0.0",
  description: "Machine Learning Integration Bridge for Velo Plot",
  provides: ["ml", "prediction", "inference"],
  tags: ["ml", "ai", "analysis"],
};

export function PluginMLIntegration(
  userConfig: Partial<PluginMLIntegrationConfig> = {}
): ChartPlugin<PluginMLIntegrationConfig> {
  let ctx: PluginContext | null = null;
  const models = new Map<string, MLModelAPI>();
  const activeVisualizations = new Map<string, { result: PredictionResult, config: VisualizationConfig }>();

  // Register initial models from config
  if (userConfig.models) {
    userConfig.models.forEach(m => models.set(m.id, createNativeModel(m)));
  }

  const api: MLIntegrationAPI & Record<string, unknown> = {
    registerModel(model: MLModelAPI) {
      if (!model.id) {
        ctx?.log.error("Cannot register model without id");
        return;
      }
      models.set(model.id, model);
      ctx?.log.info(`Model registered: ${model.name || 'Unnamed'} (${model.id})`);
    },

    async runInference(modelId: string, seriesId: string) {
      const model = models.get(modelId);
      if (!model) throw new Error(`Model not found: ${modelId}`);

      const seriesData = ctx?.data.getSeriesData(seriesId);
      if (!seriesData) throw new Error(`Series not found: ${seriesId}`);

      // Convert to Float32Array for model compatibility
      const data = new Float32Array(seriesData.y);

      const result = await model.predict({ data });
      // Attach X values for visualization
      result.xValues = seriesData.x;
      return result;
    },

    visualizeResults(result: PredictionResult, config: VisualizationConfig = {}) {
      const vizId = `ml-viz-${Math.random().toString(36).substr(2, 9)}`;
      activeVisualizations.set(vizId, { 
        result, 
        config: { ...userConfig.defaultVisualization, ...config } 
      });
      ctx?.requestRender();
      return vizId;
    },

    clearResults(visualizationId?: string) {
      if (visualizationId) {
        activeVisualizations.delete(visualizationId);
      } else {
        activeVisualizations.clear();
      }
      ctx?.requestRender();
    },

    /**
     * Alias for {@link visualizeResults} that reads as an intent-revealing
     * name for the prediction-overlay use case (task 3.18).
     */
    visualizePredictions(result: PredictionResult, config: VisualizationConfig = {}) {
      return api.visualizeResults(result, config);
    },

    /**
     * Train a small regression model on the fly (task 3.17). Creates a native
     * linear-regression model when the id does not yet exist. Returns fit
     * diagnostics including residuals for a residual plot.
     */
    trainModel(modelId: string, data: { x: number[][]; y: number[] }) {
      let model = models.get(modelId) as any;
      if (!model) {
        model = createNativeModel({ id: modelId, name: modelId, type: 'linear-regression' });
        models.set(modelId, model);
      }
      if (typeof model.train !== 'function') {
        throw new Error(`Model '${modelId}' does not support training`);
      }
      return model.train(data);
    },

    /** Retrieve the last training diagnostics for a model, if any. */
    getTrainingResult(modelId: string) {
      const model = models.get(modelId) as any;
      return model?.getTrainingResult?.() ?? null;
    },

    /** List registered model descriptors (for the ML audit / introspection). */
    listModels() {
      return Array.from(models.values()).map((m) => m.getInfo());
    },

    stats: {
      fft: nativeFFT,
      mean: nativeMean,
      standardDeviation: nativeStandardDeviation,
      correlation: nativeCorrelation
    }
  };

  function drawMLVisualizations(pCtx: PluginContext) {
    const { render, coords } = pCtx;
    const { ctx2d } = render;
    if (!ctx2d) return;

    activeVisualizations.forEach((viz) => {
      const { result, config } = viz;
      const { output, xValues, confidence } = result;
      if (!xValues) return;

      const n = Math.min(xValues.length, output.length);

      ctx2d.save();

      // 1. Draw Confidence Interval (as filled area)
      if (config.showConfidenceInterval && confidence && confidence.length === n) {
        ctx2d.beginPath();
        const opacity = config.intervalOpacity ?? 0.2;
        ctx2d.fillStyle = (config.lineStyle?.color || '#3b82f6') + Math.floor(opacity * 255).toString(16).padStart(2, '0');
        
        // Forward: upper bound
        for (let i = 0; i < n; i++) {
          const px = coords.dataToPixelX(xValues[i]);
          const py = coords.dataToPixelY(output[i] + confidence[i]);
          if (i === 0) ctx2d.moveTo(px, py);
          else ctx2d.lineTo(px, py);
        }
        
        // Backward: lower bound
        for (let i = n - 1; i >= 0; i--) {
          const px = coords.dataToPixelX(xValues[i]);
          const py = coords.dataToPixelY(output[i] - confidence[i]);
          ctx2d.lineTo(px, py);
        }
        
        ctx2d.closePath();
        ctx2d.fill();
      }

      // 2. Draw Prediction Line
      ctx2d.beginPath();
      ctx2d.strokeStyle = config.lineStyle?.color || '#3b82f6';
      ctx2d.lineWidth = config.lineStyle?.width || 2;
      if (config.lineStyle?.dash) ctx2d.setLineDash(config.lineStyle.dash);

      for (let i = 0; i < n; i++) {
        const px = coords.dataToPixelX(xValues[i]);
        const py = coords.dataToPixelY(output[i]);
        if (i === 0) ctx2d.moveTo(px, py);
        else ctx2d.lineTo(px, py);
      }
      ctx2d.stroke();

      ctx2d.restore();
    });
  }

  return {
    manifest: manifestML,

    onInit(c: PluginContext) {
      ctx = c;
    },

    onRenderOverlay(pCtx: PluginContext, _event: AfterRenderEvent) {
      drawMLVisualizations(pCtx);
    },

    onExportSVG(svgCtx) {
      if (!svgCtx.builder || svgCtx.exportContext?.options.includeOverlays === false) return;
      activeVisualizations.forEach((viz) => {
        const { result, config } = viz;
        const { output, xValues, confidence } = result;
        if (!xValues?.length) return;
        const n = Math.min(xValues.length, output.length);
        const points = Array.from({ length: n }, (_, i) => ({ x: xValues[i], y: output[i] }));
        const ci =
          config.showConfidenceInterval && confidence?.length === n
            ? {
                upper: points.map((p, i) => ({ x: p.x, y: p.y + confidence[i] })),
                lower: points.map((p, i) => ({ x: p.x, y: p.y - confidence[i] })),
              }
            : undefined;
        exportForecastOverlay(svgCtx, points, ci, config.lineStyle?.color ?? "#3b82f6");
      });
    },

    api
  };
}

export default PluginMLIntegration;

export type { 
  PluginMLIntegrationConfig, 
  MLIntegrationAPI, 
  MLModelAPI, 
  PredictionResult, 
  VisualizationConfig 
} from './types';